import type { FC } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Skeleton, Typography } from '@mui/material';
import { ReadOnlyTextField } from 'components/common';
import datadogActions from 'lib/datadog';
import { getApiErrorMessage } from 'lib/api';
import { usePinTexts } from '../api';
import type { FlowPinTextResult } from '../api';
import usePinterestAdGroupsData from '../hooks/usePinterestAdGroupsData';
import { PINTEREST_PIN_TEXT_LOCALE } from '../constants';

export const PIN_TEXT_ERROR_MESSAGE = 'Default pin text could not be loaded';
export const PIN_TEXT_HINT =
  'Pin title and description are applied by the uploader; this is the default text linked to the flow.';

interface FlowAdTextProps {
  result: FlowPinTextResult;
  adGroupNames: string[];
}

const FlowAdText: FC<FlowAdTextProps> = memo(({ result, adGroupNames }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: 2,
      p: 2,
      borderBottom: 1,
      borderColor: 'divider',
    }}
    data-testid={`pinterest-ad-text-${result.flow}`}
  >
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <Typography variant="subtitle1" fontWeight="medium">
        {result.flow}
      </Typography>
      {adGroupNames.map((name) => (
        <Typography key={name} variant="caption" color="text.secondary" display="block">
          {name}
        </Typography>
      ))}
    </Box>

    <Box sx={{ flex: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {result.isLoading ? (
        <>
          <Skeleton height={40} sx={{ transform: 'translateY(0)', borderRadius: '8px' }} />
          <Skeleton height={40} sx={{ transform: 'translateY(0)', borderRadius: '8px' }} />
        </>
      ) : result.error || !result.data ? (
        <Alert severity="warning" data-testid={`pinterest-ad-text-error-${result.flow}`}>
          {PIN_TEXT_ERROR_MESSAGE}: {getApiErrorMessage(result.error, 'unknown error')}
        </Alert>
      ) : (
        <>
          <ReadOnlyTextField label={`Title (${result.data.locale.toUpperCase()})`} value={result.data.title} />
          <ReadOnlyTextField
            label={`Description (${result.data.locale.toUpperCase()})`}
            value={result.data.description}
          />
        </>
      )}
    </Box>
  </Box>
));

FlowAdText.displayName = 'FlowAdText';

const AdTextsStep: FC = memo(() => {
  useFormContext();
  const app = (useWatch({ name: 'app' }) as string | undefined) ?? '';
  const { selectedFlows, selectedAdGroupRows } = usePinterestAdGroupsData();

  const results = usePinTexts({ app, flows: selectedFlows, locale: PINTEREST_PIN_TEXT_LOCALE });

  const adGroupNamesByFlow = useMemo(() => {
    const byFlow = new Map<string, string[]>();
    selectedAdGroupRows.forEach((row) => {
      if (!row.flow) return;
      byFlow.set(row.flow, [...(byFlow.get(row.flow) ?? []), row.ad_group_name]);
    });
    return byFlow;
  }, [selectedAdGroupRows]);

  // Report each failed flow once, however often the step re-renders.
  const reportedFlowsRef = useRef(new Set<string>());

  useEffect(() => {
    results.forEach((result) => {
      if (!result.error || reportedFlowsRef.current.has(result.flow)) return;
      reportedFlowsRef.current.add(result.flow);
      datadogActions.trackDatadogError(result.error as Error, {
        source: 'pinterest_pin_text_query',
        app,
        flow: result.flow,
      });
    });
  }, [results, app]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Ad
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {PIN_TEXT_HINT}
      </Typography>
      {results.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No flows selected
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
          {results.map((result) => (
            <FlowAdText key={result.flow} result={result} adGroupNames={adGroupNamesByFlow.get(result.flow) ?? []} />
          ))}
        </Box>
      )}
    </Box>
  );
});

AdTextsStep.displayName = 'AdTextsStep';

export default AdTextsStep;
